import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
  fetchTransactionDetail,
  fetchTransactions,
  fetchBillingOrders,
  fetchBillingOrderDetail,
  fetchCapturedAfterRelease,
} from "@/lib/transactions-api";
import type {
  CapturedAfterReleaseParams,
  TransactionsListParams,
} from "@/types/transactions";

export function useTransactions(params: TransactionsListParams) {
  return useQuery({
    queryKey: queryKeys.transactions.list(params),
    queryFn: () => fetchTransactions(params),
    staleTime: 60 * 1000,
  });
}

export function useBillingOrders(
  params: TransactionsListParams,
  enabled: boolean
) {
  return useQuery({
    queryKey: [...queryKeys.transactions.all, "billing-orders", params],
    queryFn: () => fetchBillingOrders(params),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useCapturedAfterRelease(
  params: CapturedAfterReleaseParams,
  enabled: boolean
) {
  return useQuery({
    queryKey: [...queryKeys.transactions.all, "captured-after-release", params],
    queryFn: () => fetchCapturedAfterRelease(params),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useBillingOrderDetail(orderId?: string | null, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.transactions.all, "billing-order", orderId],
    queryFn: () => fetchBillingOrderDetail(orderId || ""),
    enabled: enabled && Boolean(orderId),
    staleTime: 60 * 1000,
  });
}

export function useTransactionDetail(transactionId?: string | null) {
  return useQuery({
    queryKey: queryKeys.transactions.detail(transactionId || ""),
    queryFn: () => fetchTransactionDetail(transactionId || ""),
    enabled: Boolean(transactionId),
    staleTime: 60 * 1000,
  });
}
